import { Interpretation, InterpretationCreate, InterpretationUpdate, PHILOSOPHIES, LANGUAGES } from '../types/interpretation';
import { apiClient } from './sutras';

export const getInterpretations = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  language?: string,
  philosophy?: string
): Promise<Interpretation[]> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  // If no specific philosophy is requested, fetch all philosophies
  if (!philosophy) {
    const allInterpretations: Interpretation[] = [];
    for (const phil of Object.values(PHILOSOPHIES)) {
      // If no specific language is requested, fetch all languages
      if (!language) {
        for (const lang of Object.values(LANGUAGES)) {
          try {
            const url = `/sutras/${projectName}/${section}/${chapter}/${number}/interpretation?phil=${phil}&lang=${lang}`;
            console.log('Fetching interpretations for philosophy:', phil, 'language:', lang, 'URL:', url);
            const response = await apiClient.get(url);
            if (response.data) {
              allInterpretations.push(response.data);
            }
          } catch (err) {
            // Ignore 404 errors for specific language/philosophy combinations
            if (!(err instanceof Error && err.message.includes('404'))) {
              console.error(`Error fetching interpretations for philosophy ${phil}, language ${lang}:`, err);
            }
          }
        }
      } else {
        // If a specific language is requested, fetch only that one
        try {
          const url = `/sutras/${projectName}/${section}/${chapter}/${number}/interpretation?phil=${phil}&lang=${language}`;
          console.log('Fetching interpretations for philosophy:', phil, 'language:', language, 'URL:', url);
          const response = await apiClient.get(url);
          if (response.data) {
            allInterpretations.push(response.data);
          }
        } catch (err) {
          // Ignore 404 errors for specific philosophy
          if (!(err instanceof Error && err.message.includes('404'))) {
            console.error(`Error fetching interpretations for philosophy ${phil}, language ${language}:`, err);
          }
        }
      }
    }
    return allInterpretations;
  }
  
  // If a specific philosophy is requested, fetch only that one
  // If no specific language is requested, fetch all languages
  if (!language) {
    const allInterpretations: Interpretation[] = [];
    for (const lang of Object.values(LANGUAGES)) {
      try {
        const url = `/sutras/${projectName}/${section}/${chapter}/${number}/interpretation?phil=${philosophy}&lang=${lang}`;
        console.log('Fetching interpretations for philosophy:', philosophy, 'language:', lang, 'URL:', url);
        const response = await apiClient.get(url);
        if (response.data) {
          allInterpretations.push(response.data);
        }
      } catch (err) {
        // Ignore 404 errors for specific language
        if (!(err instanceof Error && err.message.includes('404'))) {
          console.error(`Error fetching interpretations for philosophy ${philosophy}, language ${lang}:`, err);
        }
      }
    }
    return allInterpretations;
  }
  
  // If both philosophy and language are specified, fetch only that combination
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/interpretation?phil=${philosophy}&lang=${language}`;
  console.log('GET Interpretations URL:', url);
  const response = await apiClient.get(url);
  
  // Handle both single object and array responses
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  } else if (data && typeof data === 'object') {
    return [data];
  } else {
    console.warn('Unexpected response format:', data);
    return [];
  }
};

export const createInterpretation = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  interpretation: InterpretationCreate
): Promise<Interpretation> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  const requestBody = { ...interpretation, section };
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/interpretation`;
  console.log('CREATE Interpretation URL:', url);
  console.log('CREATE Interpretation Data:', requestBody);
  const response = await apiClient.post(url, requestBody);
  return response.data;
};

export const updateInterpretation = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  interpretation: InterpretationUpdate
): Promise<Interpretation> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  const requestBody = { ...interpretation, section };
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/interpretation?lang=${interpretation.language}&phil=${interpretation.philosophy}`;
  console.log('UPDATE Interpretation URL:', url);
  console.log('UPDATE Interpretation Data:', requestBody);
  const response = await apiClient.put(url, requestBody);
  return response.data;
};

export const deleteInterpretation = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  language: string,
  philosophy: string
): Promise<void> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/interpretation?lang=${language}&phil=${philosophy}`;
  console.log('DELETE Interpretation URL:', url);
  await apiClient.delete(url);
}; 